import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import '../job-board.css';
import { jbApi } from '../api';
import { useJBAuth } from '../auth';
import SeekerGuard from '../components/SeekerGuard';
import ImageKitUpload from '../components/ImageKitUpload';
import { ikProfile } from '../utils/imagekit';

const SECTORS = [
  'Healthcare', 'Government', 'Education', 'Business & Finance',
  'Technology & AI', 'Workforce & Labor', 'Nonprofit', 'Legal',
  'Engineering', 'Creative & Media', 'Other',
];

const EXP_LEVELS = [
  { value:'entry', label:'Entry Level' },
  { value:'mid', label:'Mid Level' },
  { value:'senior', label:'Senior' },
  { value:'director', label:'Director' },
  { value:'executive', label:'Executive' },
];

function ProfileContent({ auth }) {
  const { user, token, login } = auth;
  const [form, setForm]     = useState({
    first_name: '', last_name: '', phone: '', location: '',
    headline: '', bio: '', resume_url: '', profile_photo_url: '',
    linkedin_url: '', portfolio_url: '', sectors_of_interest: [],
    experience_level: '', open_to_relocation: false,
  });
  const [loading, setLoading]   = useState(false);
  const [saved, setSaved]       = useState(false);
  const [error, setError]       = useState('');

  // Populate form from user profile
  useEffect(() => {
    if (!user) return;
    setForm({
      first_name:          user.first_name          || '',
      last_name:           user.last_name           || '',
      phone:               user.phone               || '',
      location:            user.location            || '',
      headline:            user.headline            || '',
      bio:                 user.bio                 || '',
      resume_url:          user.resume_url          || '',
      profile_photo_url:   user.profile_photo_url   || '',
      linkedin_url:        user.linkedin_url        || '',
      portfolio_url:       user.portfolio_url       || '',
      sectors_of_interest: user.sectors_of_interest || [],
      experience_level:    user.experience_level    || '',
      open_to_relocation:  user.open_to_relocation  ?? false,
    });
  }, [user]);

  const set = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  function toggleSector(s) {
    setForm(p => ({
      ...p,
      sectors_of_interest: p.sectors_of_interest.includes(s)
        ? p.sectors_of_interest.filter(x => x !== s)
        : [...p.sectors_of_interest, s],
    }));
  }

  async function handleSave(e) {
    e.preventDefault();
    setError(''); setLoading(true); setSaved(false);
    try {
      await jbApi.updateSeeker({
        first_name:          form.first_name || undefined,
        last_name:           form.last_name  || undefined,
        phone:               form.phone      || undefined,
        location:            form.location   || undefined,
        headline:            form.headline   || undefined,
        bio:                 form.bio        || undefined,
        resume_url:          form.resume_url || undefined,
        profile_photo_url:   form.profile_photo_url || undefined,
        linkedin_url:        form.linkedin_url    || undefined,
        portfolio_url:       form.portfolio_url   || undefined,
        sectors_of_interest: form.sectors_of_interest,
        experience_level:    form.experience_level || undefined,
        open_to_relocation:  form.open_to_relocation,
      }, token);
      // Refresh profile in auth context
      const me = await jbApi.me(token);
      login(token, me.role, me.profile);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.detail || 'Failed to save profile. Please try again.');
    } finally { setLoading(false); }
  }

  return (
    <div className="jb-page" data-testid="seeker-profile-page">
      {/* Portal Header */}
      <div style={{ background:'#0D2137', padding:'16px 0' }}>
        <div style={{ maxWidth:1100, margin:'0 auto', padding:'0 24px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <Link to="/seeker/dashboard" style={{ fontSize:11, color:'rgba(245,240,232,.6)', textDecoration:'none' }}>← Dashboard</Link>
            <div style={{ fontSize:16, fontWeight:700, color:'#F5F0E8', marginTop:2 }}>My Profile</div>
          </div>
        </div>
      </div>

      {/* Nav Tabs */}
      <div style={{ background:'#fff', borderBottom:'1px solid #ddd8cc' }}>
        <div style={{ maxWidth:1100, margin:'0 auto', padding:'0 24px', display:'flex', gap:4 }}>
          {[
            { label:'Dashboard', to:'/seeker/dashboard' },
            { label:'My Profile', to:'/seeker/profile' },
          ].map(({ label, to }) => (
            <Link key={to} to={to} style={{
              padding:'14px 16px', fontSize:13, fontWeight:600,
              color: window.location.pathname === to ? '#1B6B72' : '#5a6373',
              borderBottom: window.location.pathname === to ? '2px solid #1B6B72' : '2px solid transparent',
              textDecoration:'none', whiteSpace:'nowrap',
            }}>{label}</Link>
          ))}
        </div>
      </div>

      <div style={{ maxWidth:900, margin:'0 auto', padding:'28px 24px 60px' }}>
        {saved && (
          <div style={{ background:'#f0fdf4', border:'1px solid #86efac', borderRadius:10, padding:'12px 18px', fontSize:13, fontWeight:600, color:'#166534', marginBottom:20 }} data-testid="profile-saved-msg">
            Profile saved successfully.
          </div>
        )}
        {error && <div className="jb-error-box" data-testid="profile-error">{error}</div>}

        <form onSubmit={handleSave} data-testid="seeker-profile-form">

          {/* Basic Info */}
          <div style={{ background:'#fff', borderRadius:12, padding:'22px 24px', boxShadow:'0 2px 8px rgba(13,33,55,.07)', marginBottom:20 }}>
            <h2 style={{ margin:'0 0 18px', fontSize:15, fontWeight:700, color:'#0D2137' }}>Basic Information</h2>

            <div className="jb-form-row">
              <div className="jb-form-group">
                <label className="jb-form-label">First Name</label>
                <input className="jb-form-input" value={form.first_name} onChange={set('first_name')} placeholder="Alex" data-testid="profile-first-name" />
              </div>
              <div className="jb-form-group">
                <label className="jb-form-label">Last Name</label>
                <input className="jb-form-input" value={form.last_name} onChange={set('last_name')} placeholder="Johnson" data-testid="profile-last-name" />
              </div>
            </div>

            <div className="jb-form-row">
              <div className="jb-form-group">
                <label className="jb-form-label">Phone</label>
                <input className="jb-form-input" type="tel" value={form.phone} onChange={set('phone')} placeholder="+1 (555) 000-0000" data-testid="profile-phone" />
              </div>
              <div className="jb-form-group">
                <label className="jb-form-label">Location</label>
                <input className="jb-form-input" value={form.location} onChange={set('location')} placeholder="Atlanta, GA" data-testid="profile-location" />
              </div>
            </div>

            <div className="jb-form-group">
              <label className="jb-form-label">Professional Headline</label>
              <input className="jb-form-input" value={form.headline} onChange={set('headline')} placeholder="e.g. Healthcare Administration Graduate · Open to Relocation" data-testid="profile-headline" />
            </div>

            <div className="jb-form-group">
              <label className="jb-form-label">Bio</label>
              <textarea className="jb-form-input" rows={3} value={form.bio} onChange={set('bio')} placeholder="A brief summary about yourself and your career goals…" data-testid="profile-bio" style={{ resize:'vertical' }} />
            </div>
          </div>

          {/* Resume & Links */}
          <div style={{ background:'#fff', borderRadius:12, padding:'22px 24px', boxShadow:'0 2px 8px rgba(13,33,55,.07)', marginBottom:20 }}>
            <h2 style={{ margin:'0 0 6px', fontSize:15, fontWeight:700, color:'#0D2137' }}>Resume & Links</h2>
            <p style={{ fontSize:12, color:'#5a6373', marginBottom:18, marginTop:0 }}>
              Upload your resume (PDF, DOC, DOCX — max 20 MB) or paste a link directly.
            </p>

            <div className="jb-form-group">
              <label className="jb-form-label">Resume</label>
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                <ImageKitUpload
                  folder="resumes/"
                  accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  label="Upload Resume (PDF / DOC)"
                  token={token}
                  onUploaded={url => setForm(p => ({ ...p, resume_url: url }))}
                  data-testid="resume-upload"
                />
                <input
                  className="jb-form-input"
                  type="url"
                  value={form.resume_url}
                  onChange={set('resume_url')}
                  placeholder="Or paste URL: https://drive.google.com/file/d/…"
                  data-testid="profile-resume-url"
                />
              </div>
            </div>

            <div className="jb-form-group" style={{ marginTop:14 }}>
              <label className="jb-form-label">Profile Photo</label>
              <div style={{ display:'flex', alignItems:'center', gap:14 }}>
                {form.profile_photo_url && (
                  <img src={ikProfile(form.profile_photo_url)} alt="Profile" style={{ width:52, height:52, borderRadius:'50%', objectFit:'cover', border:'2px solid #ddd8cc' }} />
                )}
                <ImageKitUpload
                  folder="profiles/"
                  accept="image/png,image/jpeg,image/webp"
                  label="Upload Photo"
                  token={token}
                  onUploaded={url => setForm(p => ({ ...p, profile_photo_url: url }))}
                  data-testid="photo-upload"
                />
              </div>
            </div>

            <div className="jb-form-row" style={{ marginTop:14 }}>
              <div className="jb-form-group">
                <label className="jb-form-label">LinkedIn URL</label>
                <input className="jb-form-input" type="url" value={form.linkedin_url} onChange={set('linkedin_url')} placeholder="https://linkedin.com/in/yourname" data-testid="profile-linkedin" />
              </div>
              <div className="jb-form-group">
                <label className="jb-form-label">Portfolio / Website</label>
                <input className="jb-form-input" type="url" value={form.portfolio_url} onChange={set('portfolio_url')} placeholder="https://yourportfolio.com" data-testid="profile-portfolio" />
              </div>
            </div>
          </div>

          {/* Career Profile */}
          <div style={{ background:'#fff', borderRadius:12, padding:'22px 24px', boxShadow:'0 2px 8px rgba(13,33,55,.07)', marginBottom:24 }}>
            <h2 style={{ margin:'0 0 18px', fontSize:15, fontWeight:700, color:'#0D2137' }}>Career Profile</h2>

            <div className="jb-form-row">
              <div className="jb-form-group">
                <label className="jb-form-label">Experience Level</label>
                <select className="jb-form-input" value={form.experience_level} onChange={set('experience_level')} data-testid="profile-exp-level">
                  <option value="">Select level</option>
                  {EXP_LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                </select>
              </div>
              <div className="jb-form-group" style={{ display:'flex', alignItems:'center', gap:10, paddingTop:28 }}>
                <input type="checkbox" id="reloc" checked={form.open_to_relocation} onChange={e => setForm(p => ({ ...p, open_to_relocation: e.target.checked }))} data-testid="profile-relocation" style={{ width:16, height:16, accentColor:'#1B6B72', cursor:'pointer' }} />
                <label htmlFor="reloc" style={{ fontSize:13, color:'#374151', cursor:'pointer', fontFamily:'Inter,sans-serif' }}>Open to relocation</label>
              </div>
            </div>

            <div className="jb-form-group">
              <label className="jb-form-label" style={{ marginBottom:10, display:'block' }}>Sectors of Interest</label>
              <div style={{ display:'flex', flexWrap:'wrap', gap:8 }} data-testid="sectors-selector">
                {SECTORS.map(s => {
                  const active = form.sectors_of_interest.includes(s);
                  return (
                    <button key={s} type="button" onClick={() => toggleSector(s)} data-testid={`sector-${s.replace(/\s/g,'-').toLowerCase()}`} style={{
                      padding:'6px 14px', borderRadius:999, fontSize:12, fontWeight:600, cursor:'pointer',
                      border: active ? '1.5px solid #1B6B72' : '1.5px solid #ddd8cc',
                      background: active ? 'rgba(27,107,114,.1)' : '#fff',
                      color: active ? '#1B6B72' : '#5a6373',
                      fontFamily:'Inter,sans-serif',
                      transition:'all .15s',
                    }}>
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <button className="jb-btn-primary" type="submit" disabled={loading} style={{ minWidth:160 }} data-testid="save-profile-btn">
            {loading ? 'Saving…' : 'Save Profile'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function SeekerProfilePage() {
  const auth = useJBAuth();
  return <SeekerGuard><ProfileContent auth={auth} /></SeekerGuard>;
}
